import React from 'react';
import { DragSource, DropTarget } from 'react-dnd';
import { findDOMNode } from 'react-dom';
import Task from './component';
import { getEmptyImage } from 'react-dnd-html5-backend';
import placeholder from '../placeholder';
import block from 'bem-cn-lite';
import usingIE from './usingIE';
// Drag sources and drop targets only interact
// if they have the same string type.
// You want to keep types in a separate file with
// the rest of your app's constants.
const Types = {
    CARD: 'card'
};

/**
 * Specifies the drag source contract.
 * Only `beginDrag` function is required.
 */
const cardSource = {
    canDrag(props) {
        // You can disallow drag based on props
        return !props.isForbidden;
    },

    isDragging(props, monitor) {
        // If your component gets unmounted while dragged
        // (like a card in Kanban board dragged between lists)
        // you can implement something like this to keep its
        // appearance dragged:
        return monitor.getItem().id === props.id;
    },

    beginDrag(props, monitor, component) {
        // Return the data describing the dragged item
        component.props.onDrag(props.id);
        placeholder.setHeight(component.state.offsetHeight);

        const item = props;
        return item;
    },

    endDrag(props, monitor, component) {
        // component.setState({before: false, after: false})
        if (!monitor.didDrop()) {
            // You can check whether the drop was successful
            // or if the drag ended but nobody handled the drop
            return;
        }

        // When dropped on a compatible target, do something.
        // Read the original dragged item from getItem():
        // const item = monitor.getItem();

        // You may also read the drop result from the drop target
        // that handled the drop, if it returned an object from
        // its drop() method.
        // const dropResult = monitor.getDropResult();

        // This is a good place to call some Flux action
        // CardActions.moveCardToList(item.id, dropResult.listId);
    }
};
const cardTarget = {
    drop(props, monitor, component) {
        component.setState({ before: false, after: false });
        if (monitor.didDrop()) {
            // If you want, you can check whether some nested
            // target already handled drop
            return;
        }

        // Obtain the dragged item
        const item = monitor.getItem();

        // You can do something with it
        // ChessActions.movePiece(item.fromPosition, props.position);

        // You can also do nothing and return a drop result,
        // which will be available as monitor.getDropResult()
        // in the drag source's endDrag() method
        // component.props.onChange(item.id, props.id);
        component.props.onChange(
            item.id,
            component.props.id,
            placeholder.before
        );
        return { moved: true };
    },
    hover(props, monitor, component) {
        const dragIndex = monitor.getItem().id;
        const hoverIndex = props.id;

        // Don't replace items with themselves
        if (dragIndex === hoverIndex) {
            return;
        }

        // Determine rectangle on screen
        const hoverBoundingRect = findDOMNode(
            component
        ).getBoundingClientRect();

        // Get vertical middle
        const hoverMiddleY =
            (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

        // Determine mouse position
        const clientOffset = monitor.getClientOffset();

        // Get pixels to the top
        const hoverClientY = clientOffset.y - hoverBoundingRect.top;

        // Only perform the move when the mouse has crossed half of the items height
        // When dragging downwards, only move when the cursor is below 50%
        // When dragging upwards, only move when the cursor is above 50%

        // Dragging downwards
        if (hoverClientY < hoverMiddleY) {
            if (placeholder.move(hoverIndex, true)) {
                component.setState({
                    before: true,
                    after: false,
                    height: placeholder.height
                });
            }

            return;
        }

        // Dragging upwards
        if (hoverClientY > hoverMiddleY) {
            if (placeholder.move(hoverIndex, false)) {
                component.setState({
                    after: true,
                    before: false,
                    height: placeholder.height
                });
            }
            return;
        }

        // Time to actually perform the action
        // props.moveCard(dragIndex, hoverIndex);
        // Note: we're mutating the monitor item here!
        // Generally it's better to avoid mutations,
        // but it's good here for the sake of performance
        // to avoid expensive index searches.
    }
};

/**
 * Specifies which props to inject into your component.
 */
function collect(connect, monitor) {
    return {
        // Call this function inside render()
        // to let React DnD handle the drag events:
        connectDragSource: connect.dragSource(),
        connectDragPreview: connect.dragPreview(),
        // You can ask the monitor about the current drag state:
        isDragging: monitor.isDragging()
    };
}

const b = block('Placeholder');

class Card extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            placeholderHeight: 100,
            offsetHeight: 0,
            before: false,
            after: true
        };
    }
    componentDidMount() {
        // Use empty image as a drag preview so browsers don't draw it
        // and we can draw whatever we want on the custom drag layer instead.
        this.props.connectDragPreview(getEmptyImage(), {
            // IE fallback: specify that we'd rather screenshot the node
            // when it already knows it's being dragged so we can hide it with CSS.
            captureDraggingState: true
        });
        setTimeout(() => {
            this.setState({ offsetHeight: this.refs.container.offsetHeight });
        }, 300);
    }
    render() {
        // Your component receives its own props as usual

        // These props are injected by React DnD,
        // as defined by your `collect` function above:
        const {
            isDragging,
            connectDragSource,
            connectDropTarget,
            isOver
        } = this.props;
        const { before, after, height } = this.props;
        const isUsingEdgeOrIE = usingIE();
        return connectDropTarget(
            connectDragSource(
                <div
                    style={{
                        paddingTop: '9px',
                        display:
                            isDragging && isUsingEdgeOrIE ? 'none' : 'block'
                    }}
                >
                    <div ref="container" style={{ position: 'relative' }}>
                        <div
                            className={b({ position: 'before' })}
                            style={{
                                height: `${height}px`,
                                display: isOver && before ? 'block' : 'none'
                            }}
                        />
                        {isDragging ? <div className={b('dragged')} /> : null}
                        <Task {...this.props}  />
                        <div
                            className={b({ position: 'after' })}
                            style={{
                                height: `${height}px`,
                                display: isOver && after ? 'block' : 'none'
                            }}
                        />
                    </div>
                </div>
            )
        );
    }
}

export default DropTarget(Types.CARD, cardTarget, (connect, monitor) => ({
    connectDropTarget: connect.dropTarget(),
    isOver: monitor.isOver({ shallow: true })
}))(DragSource(Types.CARD, cardSource, collect)(Card));
